/* eslint-disable complexity */
import React, { useRef, Children } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import css from 'styled-jsx/css';
import useOnScreen from '@lmig/dotcom-aspect-hooks/useOnScreen';
import useEnvironmentalizeLink from '@lmig/dotcom-aspect-hooks/useEnvironmentalizeLink';
import Head from 'next/head';
import getConfig from 'next/config';

const {
  publicRuntimeConfig: { layoutDebugging },
} = getConfig();

const getBaseStyles = () => css.resolve`
  img {
    display: block;
    max-width: 100%;
    height: auto;
    transition: opacity 0.3s;
    opacity: 1;

    &.deferred {
      opacity: 0;

      // When the image is deferred with no src we hide the
      //   alt text from displaying and no height so the inline
      //   percentage padding gives the image the appropriate ratio
      height: 0;
      text-indent: 100%;
      white-space: nowrap;
      overflow: hidden;
    }
  }
`;

const getTypeStyles = type => {
  if (type === 'circle') {
    return css.resolve`
      * {
        border-radius: 50%;
      }
    `;
  }

  return { className: '', styles: '' };
};

/**
 * A utility component for handling deferred loading, responsiveness, environmentalization, etc.
 *
 * @param {Boolean|undefined} deferLoad
 * @param {String} type
 * @param {String} imageDomain
 */
const Image = ({
  gridSizes = undefined,
  scaleSrc = false,
  type = '',
  imageDomain = '',
  deferLoad = true,
  preload = false,
  className = '',
  children = undefined,
  useOnScreenOptions = {
    rootMargin: '0px',
    onlyOnce: true,
    renderOnServer: false,
  },
  ...rest
}) => {
  const imageRef = useRef();
  const shouldBeLoaded = useOnScreen(imageRef, useOnScreenOptions);
  const imgElement = children ? Children.only(children) : <img />; // eslint-disable-line jsx-a11y/alt-text
  const originalImgSrc = imgElement.props.src || '';
  let [, imgWidth, imgHeight] = originalImgSrc.match(/_size-([0-9]+)x([0-9]+)/) || [];
  // Give priority to dimensions passed on the `<img>` itself over those within the img's src
  imgWidth = imgElement.props.width || imgWidth;
  imgHeight = imgElement.props.height || imgHeight;
  const envImageSrc = useEnvironmentalizeLink({ domain: imageDomain, link: originalImgSrc });
  const imageManagerSizes = [320, 640, 1024, 2048, 5000];
  let envImageSrcSet;

  let imgSizes = imgElement.props.sizes;

  const validImageManagerSizes = imageManagerSizes.filter(s => s < imgWidth).concat(imgWidth);

  if (scaleSrc && imgWidth && validImageManagerSizes.length > 1) {
    envImageSrcSet = validImageManagerSizes
      .map(w => {
        const querySrc = new URL(envImageSrc);
        querySrc.search = new URLSearchParams({
          imwidth: w,
          imdensity: 1,
        });

        return `${querySrc} ${w}w`;
      })
      .join(', ');

    if (typeof gridSizes === 'object' && gridSizes !== null) {
      const breakpoints = {
        base: 0,
        sm: 480,
        md: 768,
        lg: 960,
        xl: 1280,
      };

      const gridSizesAllKeys = { ...gridSizes };

      if (!gridSizesAllKeys.base) {
        gridSizesAllKeys.base = 12;
      }

      if (!gridSizesAllKeys.md) {
        gridSizesAllKeys.md = gridSizesAllKeys.sm || gridSizesAllKeys.base;
      }

      if (!gridSizesAllKeys.xl) {
        gridSizesAllKeys.xl = gridSizesAllKeys.lg || gridSizesAllKeys.md;
      }

      imgSizes = Object.entries(gridSizesAllKeys)
        .sort((a, b) => {
          const bps = Object.keys(breakpoints);
          const aIdx = bps.indexOf(a[0]);
          const bIdx = bps.indexOf(b[0]);

          return bIdx - aIdx;
        })
        .map(([media, width]) => {
          if (['xl'].includes(media)) {
            return `(min-width: ${breakpoints[media]}px) calc(calc(calc(calc(1280px - 124px) * ${width}) / 12) - 16px)`;
          }

          if (['md', 'lg'].includes(media)) {
            return `(min-width: ${breakpoints[media]}px) calc(calc(calc(calc(100vw - 124px) * ${width}) / 12) - 16px)`;
          }

          return `${media === 'sm' ? `(min-width: ${breakpoints[media]}px) ` : ''
            }calc(calc(calc(calc(100vw - 28px) * ${width}) / 12) - 8px)`;
        })
        .join(', ');
    }
  } else if (imgElement.props.srcSet) {
    const originalImgSrcSet = imgElement.props.srcSet;
    [, imgWidth, imgHeight] = (originalImgSrcSet.split(',')[0] || '').match(
      /_size-([0-9]+)x([0-9]+)/,
    ) || [null, imgWidth, imgHeight];
    const splitOriginalImgSrcSet = originalImgSrcSet
      ? originalImgSrcSet.split(',').map(s => s.trim())
      : [];

    envImageSrcSet = splitOriginalImgSrcSet
      // eslint-disable-next-line react-hooks/rules-of-hooks
      .map(l => useEnvironmentalizeLink({ domain: imageDomain, link: l }))
      .join(', ');
  }

  const imgSrc = !shouldBeLoaded && deferLoad && !preload ? undefined : envImageSrc;
  const imgSrcSet = !shouldBeLoaded && deferLoad && !preload ? undefined : envImageSrcSet;

  const { className: baseClass, styles: baseStyles } = getBaseStyles(!imgSrc);
  const { className: typeClass, styles: typeStyles } = getTypeStyles(type);

  // Calculate the aspect ratio in percent so it can be applied while the image is deferred
  const imgAspect = imgWidth && imgHeight && Math.floor((imgHeight / imgWidth) * 100);

  const preloadProps = {
    rel: 'preload',
    as: 'image',
    ...(envImageSrc && { href: envImageSrc }),
    ...(envImageSrcSet && { imagesrcset: envImageSrcSet }),
    ...(imgSizes && { imagesizes: imgSizes }),
    ...(preload && preload.min && { media: `(min-width: ${preload.min})` }),
    ...(preload && preload.max && { media: `(max-width: ${preload.max})` }),
  };

  const onload = ({ target: { src, height, width, attributes } }) => {
    if (!attributes.height || !attributes.width) {
      // eslint-disable-next-line no-console
      console.warn(
        `*WARNING* - ${src} has no height/width attributes set. Try using: height=${height} width=${width}`,
      );
    }
  };

  return (
    <>
      {preload && (
        <Head>
          <link {...preloadProps} />
        </Head>
      )}
      {React.cloneElement(imgElement, {
        ...{
          className: cx(className, typeClass, baseClass, imgElement.props.className, {
            deferred: !imgSrc && !imgSrcSet,
          }),
          decoding: 'async',
        },
        ...(!!imgSizes && { sizes: imgSizes }),
        ref: imageRef,
        ...(!!envImageSrc && { src: imgSrc }),
        ...(!!envImageSrcSet && { srcSet: imgSrcSet }),
        ...(!imgSrc && { style: { paddingTop: `${imgAspect}%`, ...imgElement.props.style } }),
        ...(imgWidth && { width: imgWidth }),
        ...(imgHeight && { height: imgHeight }),
        ...(layoutDebugging === 'true' && { onLoad: onload }),
        ...rest,
      })}
      {typeStyles}
      {baseStyles}
    </>
  );
};

Image.contentRender = ({
  imageDomain = '',
  deferLoad = true,
  preload = false,
  ...rest }) => (
  // eslint-disable-next-line jsx-a11y/alt-text
  <Image preload={preload} imageDomain={imageDomain} deferLoad={deferLoad}>
    {/* eslint-disable-next-line jsx-a11y/alt-text  */}
    <img {...rest} />
  </Image>
);

Image.contentRender.propTypes = {
  imageDomain: PropTypes.string,
  deferLoad: PropTypes.bool,
  preload: PropTypes.bool,
};


Image.propTypes = {
  gridSizes: PropTypes.shape(
    { base: PropTypes.number },
    { sm: PropTypes.number },
    { md: PropTypes.number },
    { lg: PropTypes.number },
    { xl: PropTypes.number },
  ),
  scaleSrc: PropTypes.bool,
  /** circle or default square image */
  type: PropTypes.oneOf(['', 'circle']),
  /** If environmentalization of the image src is required, which domain to use */
  imageDomain: PropTypes.string,
  /** whether loading the image should be deferred until it's in the viewport */
  deferLoad: PropTypes.bool,
  /** whether we add a preload tag to the head for the image */
  preload: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({ min: PropTypes.string }, PropTypes.shape({ max: PropTypes.string })),
  ]),
  children: PropTypes.element,
  className: PropTypes.string,
  useOnScreenOptions: PropTypes.shape({
    rootMargin: PropTypes.string,
    onlyOnce: PropTypes.bool,
    renderOnServer: PropTypes.bool,
  }),
};


export default Image;
